// src/.../DebtConsolidationCalculator/Components/CurrentMortgageCard.jsx
import React, { useMemo, useEffect, useCallback } from "react";
import FormattedNumberInput from "../HelperFunctions/formatNumber";
import {
  aprToMonthly,
  pmt,
  periodsForPayment,
} from "../Hooks/useDebtConsolidationMath";
import InfoTooltip from "../DesignSystem/InfoTooltip";

// Small helpers
const toNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const isBlank = (v) => String(v ?? "").trim() === "";
const round2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "");

export default function CurrentMortgageCard({
  value,
  onChange,
  showPreview = true,
  title = "Existing Mortgage",
  actions,
}) {
  const cm = value || {};

  // Safe setter (functional to avoid stale closures)
  const setField = useCallback(
    (key, patch = {}) =>
      (val) =>
        onChange((prev) => ({
          ...(prev || {}),
          ...patch,
          [key]: val,
        })),
    [onChange],
  );

  const L = toNum(cm.balance);

  // SIMPLE inputs
  const simpleApr = toNum(cm.apr);
  const simpleR = simpleApr > 0 ? aprToMonthly(simpleApr) : 0;

  const termTotalMonths = toNum(cm.termYears) * 12 + toNum(cm.termMonths);
  const userMonthly = toNum(cm.monthly);

  // Auto monthly from (balance + apr + term)
  const autoMonthly = useMemo(() => {
    if (L <= 0 || simpleApr <= 0 || termTotalMonths <= 0) return 0;
    const m = pmt(L, simpleR, termTotalMonths);
    return Number.isFinite(m) && m > 0 ? m : 0;
  }, [L, simpleApr, simpleR, termTotalMonths]);

  // Auto term from (balance + apr + monthly)
  const autoTermMonths = useMemo(() => {
    if (L <= 0 || simpleApr <= 0 || userMonthly <= 0) return 0;
    const exact = periodsForPayment(L, simpleR, userMonthly);
    const months = Number.isFinite(exact) ? Math.ceil(exact) : 0;
    return months > 0 ? months : 0;
  }, [L, simpleApr, simpleR, userMonthly]);

  // STEPPED inputs (uses same term fields)
  const steppedMonthsTotal = termTotalMonths;

  const steppedMonthsInitial = Math.min(
    steppedMonthsTotal || 0,
    toNum(cm.initialYears) * 12 + toNum(cm.initialMonths),
  );

  const steppedMonthsReversion = Math.max(
    0,
    (steppedMonthsTotal || 0) - steppedMonthsInitial,
  );

  const rInitial =
    toNum(cm.initialApr) > 0 ? aprToMonthly(toNum(cm.initialApr)) : 0;
  const rSVR = toNum(cm.svrApr) > 0 ? aprToMonthly(toNum(cm.svrApr)) : 0;

  // Stepped illustration payments (m0, m1)
  const steppedPreview = useMemo(() => {
    if (!L || !steppedMonthsTotal) return { m0: 0, m1: 0 };

    // m0 as if amortised over full term at initial rate (typical illustration)
    const m0 =
      steppedMonthsInitial > 0 ? pmt(L, rInitial, steppedMonthsTotal) : 0;

    // balance after initial period (amort formula)
    let bal = L;
    if (steppedMonthsInitial > 0) {
      if (rInitial === 0) {
        bal = Math.max(0, L - m0 * steppedMonthsInitial);
      } else {
        const a = Math.pow(1 + rInitial, steppedMonthsInitial);
        bal = L * a - m0 * ((a - 1) / rInitial);
      }
    }

    const m1 =
      steppedMonthsReversion > 0
        ? pmt(Math.max(0, bal), rSVR, steppedMonthsReversion)
        : 0;

    return {
      m0: Number.isFinite(m0) ? m0 : 0,
      m1: Number.isFinite(m1) ? m1 : 0,
    };
  }, [
    L,
    steppedMonthsTotal,
    steppedMonthsInitial,
    steppedMonthsReversion,
    rInitial,
    rSVR,
  ]);

  useEffect(() => {
    const canAutoMonthly =
      L > 0 && simpleApr > 0 && termTotalMonths > 0 && autoMonthly > 0;
    const monthlyBlank = isBlank(cm.monthly);
    const monthlyAuto = cm._monthlyAuto === true;

    if (canAutoMonthly && (monthlyBlank || monthlyAuto)) {
      onChange((prev) => ({
        ...(prev || {}),
        monthly: round2(autoMonthly),
        _monthlyAuto: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L, simpleApr, termTotalMonths, autoMonthly]);

  useEffect(() => {
    const canAutoTerm =
      L > 0 && simpleApr > 0 && userMonthly > 0 && autoTermMonths > 0;
    const termBlank = isBlank(cm.termYears) && isBlank(cm.termMonths);
    const termAuto = cm._termAuto === true;

    if (canAutoTerm && (termBlank || termAuto)) {
      const y = Math.floor(autoTermMonths / 12);
      const m = autoTermMonths % 12;

      onChange((prev) => ({
        ...(prev || {}),
        termYears: String(y),
        termMonths: String(m),
        _termAuto: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L, simpleApr, userMonthly, autoTermMonths]);

  useEffect(() => {
    // SVR monthly is most meaningful when we have stepped inputs + term
    const canAutoSvrMonthly =
      L > 0 &&
      steppedMonthsTotal > 0 &&
      toNum(cm.svrApr) > 0 &&
      steppedMonthsReversion > 0 &&
      steppedPreview.m1 > 0;

    const svrMonthlyBlank = isBlank(cm.svrMonthly);
    const svrMonthlyAuto = cm._svrMonthlyAuto === true;

    if (canAutoSvrMonthly && (svrMonthlyBlank || svrMonthlyAuto)) {
      onChange((prev) => ({
        ...(prev || {}),
        svrMonthly: round2(steppedPreview.m1),
        _svrMonthlyAuto: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    L,
    steppedMonthsTotal,
    steppedMonthsReversion,
    cm.svrApr,
    steppedPreview.m1,
  ]);

  return (
    <div className="dcc-panel" style={{ marginBottom: 16 }}>
      <div className="dcc-mortgage-header">
        <div className="Label-Large-Bold">{title}</div>
        <div className="dcc-mortgage-actions">{actions}</div>
      </div>

      <table className="dcc-table" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>
              <div className="Content-SemiBold">Balance (£)</div>
            </th>
            <th>
              <div className="Content-SemiBold">
                Current Monthly Payment (£)
              </div>
            </th>
            <th>
              <div className="Content-SemiBold">Current Interest Rate (%)</div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              <FormattedNumberInput
                value={cm.balance || ""}
                onChange={setField("balance")}
                className="Content dcc-input-num"
              />
            </td>

            <td>
              <FormattedNumberInput
                value={cm.monthly || ""}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...(prev || {}),
                    monthly: val,
                    _monthlyAuto: false, // user override
                  }))
                }
                className="Content dcc-input-num"
              />
            </td>

            <td>
              <FormattedNumberInput
                value={cm.apr || ""}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...(prev || {}),
                    apr: val,
                    // allow auto updates if user clears/was auto
                    // (we don't force overwrite if _monthlyAuto is false)
                  }))
                }
                className="Content dcc-input-num"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="dcc-field">
        <div className="dcc-term-grid">
          <label className="Content-SemiBold dcc-label">
            Standard Variable Payment (£)
          </label>
          <div className="dcc-label-row">
            <label className="Content-SemiBold dcc-label">
              Standard Variable Rate (%)
            </label>

            <InfoTooltip
              text="Please enter your lender’s current Standard Variable Rate (%)."
              position="top"
            />
          </div>
        </div>

        <div className="dcc-term-grid">
          <div className="dcc-field-inline">
            <FormattedNumberInput
              value={cm.svrMonthly || ""}
              onChange={(val) =>
                onChange((prev) => ({
                  ...(prev || {}),
                  svrMonthly: val,
                  _svrMonthlyAuto: false, // user override
                }))
              }
              className="Content dcc-input-num"
            />
          </div>

          <div className="dcc-field-inline">
            <FormattedNumberInput
              value={cm.svrApr || ""}
              onChange={setField("svrApr")}
              className="Content dcc-input-num"
            />
          </div>
        </div>
      </div>

      <hr className="dcc-divider" />

      <div className="dcc-term-grid">
        <div className="dcc-field">
          <label className="Content-SemiBold dcc-label">
            Remaining time on Initial Fixed Period
          </label>
          <div className="dcc-term-grid">
            <div className="dcc-field-inline">
              <FormattedNumberInput
                value={cm.initialYears || ""}
                onChange={setField("initialYears")}
                decimals={0}
                className="Content dcc-input-num"
              />
              <div className="Content-Small dcc-subtext">Years</div>
            </div>
            <div className="dcc-field-inline">
              <FormattedNumberInput
                value={cm.initialMonths || ""}
                onChange={setField("initialMonths")}
                decimals={0}
                className="Content dcc-input-num"
              />
              <div className="Content-Small dcc-subtext">Months</div>
            </div>
          </div>
        </div>

        <div className="dcc-field">
          <label className="Content-SemiBold dcc-label">Total Loan Term</label>
          <div className="dcc-term-grid">
            <div className="dcc-field-inline">
              <FormattedNumberInput
                value={cm.termYears || ""}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...(prev || {}),
                    termYears: val,
                    _termAuto: false, // user override
                  }))
                }
                decimals={0}
                className="Content dcc-input-num"
              />
              <div className="Content-Small dcc-subtext">Years</div>
            </div>
            <div className="dcc-field-inline">
              <FormattedNumberInput
                value={cm.termMonths || ""}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...(prev || {}),
                    termMonths: val,
                    _termAuto: false, // user override
                  }))
                }
                decimals={0}
                className="Content dcc-input-num"
              />
              <div className="Content-Small dcc-subtext">Months</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
